<template>
  <wui-modal
    v-if="alarm"
    :size="40"
    :closable="false"
    class="!w-[280px] phone:!w-4/5 [&_.wui-modal__header]:hidden"
  >
    <main class="px-8 py-6">
      <p class="text-center text-1624 leading-8 mb-4">
        {{ alarm.name }}
      </p>

      <div class="w-20 h-20 rounded-full animate-border-pulse-teal bg-teal flex justify-center items-center mx-auto mt-5 mb-9">
        <wui-icon
          name="bellM"
          class="text-teal-tints-600"
          xlarge
        />
      </div>

      <p class="text-2432 text-center">
        {{ alarm.time }}
      </p>

      <p class="mt-1 text-center text-1316 text-fg-base-fg22">
        {{ alarmDateDisplay }}
      </p>
    </main>

    <template #footer>
      <wui-btn
        class="grow h-10!"
        icon
        prepend-icon="refreshM"
        @click="onCloseDialog"
      >
        <span class="sr-only select-none">{{ $t('common.snooze') }}</span>
      </wui-btn>

      <terminate-button :session-id="rtcSessionFacade.session.id">
        <template #default="{ terminate, loading }">
          <wui-btn
            data-test="cancel-button"
            :disabled="loading"
            variant="negative"
            state="filled"
            icon
            class="grow"
            prepend-icon="stop"
            @click="terminate"
          >
            <span class="sr-only select-none">{{ $t('common.close') }}</span>
          </wui-btn>
        </template>
      </terminate-button>
    </template>
  </wui-modal>

  <wui-modal
    v-else
    :size="40"
    :closable="false"
    class="!w-[520px] phone:!w-4/5 relative [&_.wui-modal__header]:hidden"
  >
    <div class="absolute top-4 right-4 phone:right-2 phone:top-2 z-10">
      <wui-btn
        icon
        :size="32"
        prepend-icon="minus"
        variant="neut"
        state="alpha"
        @click="onCloseDialog()"
      >
        <span class="sr-only select-none">{{ $t('common.minimizeWindow') }}</span>
      </wui-btn>
    </div>

    <header>
      <div class="flex justify-center mb-3">
        <div class="pl-3 pr-7 max-w-[380px] mx-2 py-3 border rounded-full border-bor-15 flex items-center gap-3">
          <contact-avatar
            :name="displayName"
            :is-room="!!room?.isGroup"
            :room="room?.isGroup ? room : undefined"
            :contact="!room?.isGroup ? contact : undefined"
          />
          <div>
            <p
              class="text-heading-sb-18-24 line-clamp-1 text-fg-base-fg3 mb-[2px]"
              :title="displayName || number || ''"
            >
              {{ displayName || number }}
            </p>
            <p
              v-if="contactPositionToDisplay"
              class="text-body-r-14-20 text-fg-base-fg22 mb-[2px]"
            >
              {{ contactPositionToDisplay }}
            </p>
            <p
              v-if="groups.length || room?.isGroup"
              class="text-body-r-14-20 text-fg-base-fg22 mb-[2px]"
            >
              <template v-if="room?.isGroup">
                {{ $t('common.scheduleItem') }}
              </template>
              <template v-else>
                {{ groups.join(', ') }}
              </template>
            </p>
          </div>
        </div>
      </div>
      <h3
        v-if="state === STATE.RINGING"
        class="text-body-r-14-20 text-fg-base-fg10 mb-5 text-center"
      >
        {{ $t('common.incomingCall') }}
      </h3>
      <p
        v-if="state === STATE.RINGING && invitedByName"
        class="text-body-r-14-20 text-fg-base-fg22 -mt-3 mb-5 text-center"
      >
        {{ $t('common.callInvitedBy', { name: invitedByName }) }}
      </p>
      <h3
        v-else-if="state === STATE.PROGRESS"
        class="text-body-r-14-20 text-fg-base-fg10 mb-5 text-center"
      >
        {{ $t('common.outgoingCall') }}
      </h3>
    </header>

    <main class="relative mb-7">
      <template v-if="video && isWebcamAvailable && !exception">
        <div class="relative h-[420px]">
          <video
            ref="webcamElement"
            class="rounded-8 object-cover h-[420px]"
            :muted="true"
            :autoplay="true"
            @loadeddata="onLocalVideoLoaded"
          />
          <div
            v-if="isLocalVideoLoading"
            class="absolute inset-0 z-5 flex items-center justify-center rounded-8 bg-black/40"
          >
            <wui-icon
              name="spinner"
              class="w-8! h-8! text-white animate-spin"
            />
          </div>
        </div>
      </template>
      <div
        v-else
        class="rounded-24 bg-bg-base-bg6 flex items-center justify-center h-[420px]"
      >
        <div class="flex flex-col items-center justify-center">
          <wui-icon
            name="videoCameraOffM"
            class="text-fg-base-fg32 w-[100px]! h-[100px]!"
          />
          <p class="text-body-r-14-20 text-fg-base-fg22 text-center">
            {{ isIncomingAudioOnlyInvite ? $t('common.incomingCallStartedWithoutVideo') : $t('common.cameraDisabledOrUnavailable') }}
          </p>
        </div>
      </div>
      <div class="absolute bottom-4 left-1/2 -translate-x-1/2">
        <div class="flex gap-x-5 items-center">
          <div
            v-if="!isAudioDisabled"
          >
            <button-with-menu
              light
              :main-button-clickable="false"
              icon="micM"
              :rounded="24"
              with-menu
              :direction="Direction.TOP"
            >
              <wui-card
                class="min-w-[220px]"
                state="text"
                shadow="overflow"
              >
                <wui-list>
                  <wui-list-item
                    v-for="audioDeviceOption in audioDeviceOptions"
                    :key="audioDeviceOption.value"
                    :selected="audioDeviceOption.value === activeMicrophoneId"
                    @click="onSelectMicrophone(audioDeviceOption.value)"
                  >
                    <p class="text-body-r-15-22 text-fg-base-fg3">
                      {{ audioDeviceOption.label }}
                    </p>
                  </wui-list-item>
                </wui-list>
              </wui-card>
            </button-with-menu>
          </div>
          <div
            v-if="canSelectVideoOnAnswer && !isOutgoingAudioOnly && !room?.isGroup"
          >
            <button-with-menu
              light
              :icon="video ? 'videoCameraM' : 'videoCameraOffM'"
              :main-button-clickable="session.direction === 'incoming'"
              with-menu
              :direction="Direction.TOP"
              @button-click="toggleVideo"
            >
              <wui-card
                class="min-w-[220px]"
                state="text"
                shadow="overflow"
              >
                <wui-list>
                  <wui-list-item
                    v-for="videoDeviceOption in videoDeviceOptions"
                    :key="videoDeviceOption.value"
                    :selected="videoDeviceOption.value === activeWebcamId"
                    @click="onSelectWebcam(videoDeviceOption.value)"
                  >
                    <p class="text-body-r-15-22 text-fg-base-fg3">
                      {{ videoDeviceOption.label }}
                    </p>
                  </wui-list-item>
                </wui-list>
              </wui-card>
            </button-with-menu>
          </div>
        </div>
      </div>
    </main>

    <template #footer>
      <template v-if="state === STATE.RINGING">
        <answer-button :session-id="rtcSessionFacade.session.id">
          <template #default="{ answer }">
            <wui-btn
              class="grow"
              data-test="answer-button"
              auto-loading
              icon
              variant="positive"
              state="filled"
              :size="48"
              prepend-icon="phoneF"
              @click="answer(audio, canSelectVideoOnAnswer ? video : false)"
            >
              <span class="sr-only select-none">{{ $t('common.acceptCall') }}</span>
            </wui-btn>
          </template>
        </answer-button>
      </template>
      <terminate-button :session-id="rtcSessionFacade.session.id">
        <template #default="{ terminate, loading }">
          <wui-btn
            data-test="cancel-button"
            :disabled="loading"
            icon
            :size="48"
            variant="negative"
            state="filled"
            :prepend-icon="loading ? 'spinner' : 'phoneDownF'"
            class="grow"
            @click="terminate"
          >
            <span class="sr-only select-none">{{ $t('common.cancelCall') }}</span>
          </wui-btn>
        </template>
      </terminate-button>
    </template>
  </wui-modal>
</template>

<script lang="ts" setup>
import { closeDialogKey, Direction, safeInject, SelectItem, useRenderNotifier, WuiBtn, WuiCard, WuiIcon, WuiList, WuiListItem, WuiModal } from '@wui/common-library'
import dayjs from 'dayjs'
import { storeToRefs } from 'pinia'
import { onUnmounted, toRef, ref, computed, triggerRef, onMounted, watch } from 'vue'

import { useCallRingingStore } from '@/app/store/calls/ringing'
import { useStoreRooms } from '@/app/store/pinia/rooms/rooms'
import { useIncomingCallSpeaker, createIncomingCallSpeaker } from '@/composables/useIncomingCallSpeaker'

import { AnswerButton, TerminateButton } from '@/features/incoming-call-buttons'

import { AlarmSchedule, useStoreAlarm } from '@/entities/alarm'
import { canUserModerateRoom, getInitialAudioEnabledOnJoin, useCallSessionRoomStore } from '@/entities/call-session'
import { useCallSessionMediaStore } from '@/entities/call-session-media'
import { useCallsStore } from '@/entities/calls'
import { useStoreContacts } from '@/entities/contact'
import { ContactAvatar } from '@/entities/contact-avatar'
import { useLocalWebcamVideo, useStoreMediaDevices } from '@/entities/media-devices'
import { usePreferencesStore, PreferenceKey } from '@/entities/preference'

import { ButtonWithMenu } from '@/shared/components/buttons'
import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { useSearchGroups } from '@/shared/composables/search-handlers'
import { useNotification } from '@/shared/composables/use-notification'
import { RTCSessionFacade, STATE, useWebRTC, NO_ANSWER_TIMEOUT } from '@/shared/services/jssip'
import { Contact } from '@/shared/types/api'
import { getSessionInvitedByName } from '@/shared/utils/jssip'
import { pServedIsRoom } from '@/shared/utils/uri'

const props = defineProps<{
  rtcSessionFacade: RTCSessionFacade
}>()

const session = toRef(props, 'rtcSessionFacade')
const sessionRoomStore = useCallSessionRoomStore()
const room = computed(() => sessionRoomStore.getRoom(session.value.sessionId))
const exception = ref<DOMException | null>(null)
const { startLocalStream, webcamElement, closeWebcam } = useLocalWebcamVideo()

const { currentUser } = useCurrentUser()
const { preferences } = usePreferencesStore()
const canModerate = computed(() => canUserModerateRoom(room.value, currentUser.value?.imLogin))

const audio = ref(getInitialAudioEnabledOnJoin(room.value, {
  micDisabledOnStartConferenceGroup: !!preferences[PreferenceKey.MIC_DISABLED_ON_START_CONFERENCE_GROUP],
  pushToTalkGroup: !!preferences[PreferenceKey.PUSH_TO_TALK_GROUP],
  currentUserImLogin: currentUser.value?.imLogin,
}))
const video = ref(false)
const isLocalVideoLoading = ref(false)

const { awaitForRoomAdded } = useStoreRooms()
const { createNewIncomingCallNotification, closeNewIncomingCallNotification } = useNotification()
const { notifyError } = useRenderNotifier()
const { sessions } = useWebRTC()
const store = useStoreMediaDevices()
const { devices, activeWebcamId, activeMicrophoneId, isWebcamAvailable } = storeToRefs(store)
const { t } = useLocalization()
const { isSessionRingingEnabled, muteBySessionId } = useCallRingingStore()
const closeModal = safeInject(closeDialogKey)
const { play, stop, paused } = useIncomingCallSpeaker(createIncomingCallSpeaker())
const { callMediaRequests } = storeToRefs(useCallsStore())
const { findBySessionIdAndParticipant } = useCallSessionMediaStore()

const { findByPServed } = useStoreContacts()

const state = computed(() => props.rtcSessionFacade.sessionState.value)
const displayName = computed(() => session.value.displayName.value === 'Anonymous' ? t('common.numberIsHidden') : session.value.displayName.value)
const invitedByName = computed(() => getSessionInvitedByName(session.value.sessionId))
const number = computed(() => session.value.number)
const groups = computed(() => useSearchGroups(number.value))
const contact = computed<Contact | undefined>(() => room.value ? findByPServed(room.value.pServedUser) : undefined)
const contactPositionToDisplay = computed<Contact['position'] | false | undefined>(() => !room.value?.isGroup && contact.value?.position) // value is undefined when subscribers map is empty (external contacts or 'AON' feature callers))
const isGroup = computed(() => pServedIsRoom(room.value?.pServedUser ?? ''))
const isRinging = computed(() => {
  return isSessionRingingEnabled(session.value.sessionId)
})
const audioDeviceOptions = computed<SelectItem[]>(() => {
  return Array.from(devices.value.audioInput, ([deviceId, device]) => ({
    value: deviceId,
    label: device.label,
  }))
})

const onSelectWebcam = (value: string | number) => {
  activeWebcamId.value = String(value)
}

const videoDeviceOptions = computed<SelectItem[]>(() => {
  return Array.from(devices.value.videoInput, ([deviceId, device]) => ({
    value: deviceId,
    label: device.label,
  }))
})

const mediaRequest = computed(() => callMediaRequests.value.get(props.rtcSessionFacade.callId.value))

const isAudioDisabled = computed(() => (mediaRequest.value?.requestedAudio === false) || !canModerate.value)
const isIncomingAudioOnlyInvite = computed(() => {
  if (state.value !== STATE.RINGING || room.value?.isGroup) {
    return false
  }

  const mediaStatus = findBySessionIdAndParticipant(session.value.sessionId, session.value.number)
  return mediaStatus?.video !== true
})

const isOutgoingAudioOnly = computed(() => !video.value && state.value === STATE.PROGRESS)

const canSelectVideoOnAnswer = computed(() => !isIncomingAudioOnlyInvite.value)
const hasActiveSessionLocalVideo = computed(() => {
  const tracks = session.value.localVideoStream.value?.getVideoTracks?.() ?? []
  return tracks.some((track) => track.enabled && track.readyState === 'live')
})

const syncLocalVideoToElement = () => {
  if (!webcamElement.value || !video.value) {
    return
  }

  const stream = session.value.localVideoStream.value
  if (!stream) {
    return
  }

  webcamElement.value.srcObject = stream
}

if (session.value.number && isGroup.value) {
  awaitForRoomAdded(session.value.number)
    .then((room) => {
      useCallSessionRoomStore().linkSessionToRoom(session.value.sessionId, room)
      session.value.displayName.value = room.name
      session.value.number = room.number
      triggerRef(sessions)
    }).catch((e: unknown) => {
      const errorMessage = e instanceof Error ? e.message : t('common.unknownError')
      notifyError(errorMessage)
      session.value.terminate()
    })
}

const onCloseDialog = () => {
  muteBySessionId(session.value.sessionId)
  closeModal(undefined)
}

onMounted(() => {
  if (isRinging.value && state.value === STATE.RINGING) {
    try {
      play()
    } catch (e) {
      console.error(e)
    }
  }
  if (state.value === STATE.RINGING) {
    createNewIncomingCallNotification(displayName.value, session.value.sessionId, isGroup.value)
  }

  if (mediaRequest.value?.requestedAudio === false) {
    audio.value = false
  }
})

onUnmounted(() => {
  closeNewIncomingCallNotification(session.value.sessionId)
  closeWebcam()
  stop()
})

const openStream = () => {
  closeWebcam()
  isLocalVideoLoading.value = true
  if (exception.value) {
    exception.value = null
  }

  startLocalStream().catch((e: unknown) => {
    exception.value = e as DOMException
    isLocalVideoLoading.value = false
  })
}

const onLocalVideoLoaded = () => {
  isLocalVideoLoading.value = false
}

const onSelectMicrophone = (value: string | number) => {
  activeMicrophoneId.value = String(value)
}

watch(() => isRinging.value, () => {
  if (isRinging.value && state.value === STATE.RINGING) {
    play()
    setTimeout(() => {
      if (!paused()) {
        stop()
      }
    }, NO_ANSWER_TIMEOUT)
  } else {
    stop()
  }
})

watch(() => activeWebcamId.value, () => {
  if (video.value) {
    openStream()
  }
})

watch(() => isWebcamAvailable.value, (value) => {
  if (!video.value) {
    closeWebcam()
    return
  }

  if (value) {
    openStream()
    return
  }

  closeWebcam()
})

watch(canSelectVideoOnAnswer, (value) => {
  if (value || !video.value) {
    return
  }

  video.value = false
  isLocalVideoLoading.value = false
  closeWebcam()
})

watch(hasActiveSessionLocalVideo, (value) => {
  if (!value) {
    return
  }

  video.value = true
  isLocalVideoLoading.value = false
  syncLocalVideoToElement()
}, { immediate: true })

watch([webcamElement, () => session.value.localVideoStream.value, video], () => {
  syncLocalVideoToElement()
})

const { alarmSchedules } = storeToRefs(useStoreAlarm())
const now = dayjs().tz('Europe/Moscow').set('seconds', 0)
const alarm = computed<AlarmSchedule | null>(() => {
  if (session.value.number !== currentUser.value?.internalNumber) {
    return null
  }

  const al = Array.from(alarmSchedules.value.values()).find((alarm) => {
    if (!alarm.enabled) {
      return false
    }
    if (alarm.date) {
      const dt = dayjs(`${alarm.date} ${alarm.time}`)
      return dt.isSame(now, 'day') && dt.isSame(now, 'hour') && dt.isSame(now, 'minute')
    }

    return [[alarm.sunday, 0], [alarm.monday, 1], [alarm.tuesday, 2], [alarm.wednesday, 3], [alarm.thursday, 4], [alarm.friday, 5], [alarm.saturday, 6]]
      .filter(([val]) => !!val)
      .map((val) => val[1])
      .includes(now.day())
  })

  return al ?? null
})

const alarmDateDisplay = computed(() => {
  return alarm.value?.date ? dayjs(alarm.value.date).format(t('common.dateFormat')) : now.format(t('common.dateFormat'))
})

const toggleVideo = async () => {
  if (!canSelectVideoOnAnswer.value || session.value.direction === 'outgoing') {
    return
  }

  const nextValue = !video.value
  video.value = nextValue

  if (!nextValue) {
    isLocalVideoLoading.value = false
    closeWebcam()
    return
  }

  openStream()
}
</script>
